import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { MandatoryAdditionalStyle } from '../../components.utils';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { BlockWidths } from '../../components/pageLayouts/BlockTypes.utils';
import { createBlockStyle } from '../blockMutualStyle';
import { Kasda } from '../../../Kasda/Kasda';
import { Iframe } from './Iframe';

interface ThirdPartyEmbedDataProps {
  embedCodeHTMLString?: string;
  embedMarkup?: string;
  scriptSrcs?: string[] | string;
  dataId?: string;
  defaultBlocksWidths: BlockWidths;
}

type ThirdPartyEmbedProps = ThirdPartyEmbedDataProps & MandatoryAdditionalStyle;

const getStyle = (defaultBlocksWidths: BlockWidths) => StyleSheet.create({
  style: { ...createBlockStyle(defaultBlocksWidths) },
}).style;

// Mirrors the Qwik parseScriptSrcs: array OR JSON-encoded string, empties filtered.
const parseScriptSrcs = (scriptSrcs: unknown): string[] => {
  if (Array.isArray(scriptSrcs)) {
    return scriptSrcs.filter((src): src is string => typeof src === 'string' && src.length > 0);
  }
  if (typeof scriptSrcs !== 'string' || scriptSrcs.length === 0) return [];
  try {
    const parsed = JSON.parse(scriptSrcs);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((src): src is string => typeof src === 'string' && src.length > 0);
  } catch {
    return [];
  }
};

export const ThirdPartyEmbed: React.FunctionComponent<ThirdPartyEmbedProps> = props => {
  const { embedCodeHTMLString, embedMarkup, scriptSrcs, dataId, additionalStyle, defaultBlocksWidths } = props;
  const style = getStyle(defaultBlocksWidths);

  const primaryHtml = (embedCodeHTMLString ?? '').trim();
  const markup = (embedMarkup ?? '').trim();
  const srcs = parseScriptSrcs(scriptSrcs);

  const hasScripts = srcs.length > 0;
  const hasMarkup = markup.length > 0;
  const hasPrimaryHtml = primaryHtml.length > 0;

  // Loaded in every mode when scripts are present (ungated, deduped via Kasda).
  const scriptTags = hasScripts ? (
    <Kasda.Script>
      {srcs.map(src => <script key={src} async src={src} />)}
    </Kasda.Script>
  ) : null;

  // Mode 2: self-injecting script-only embed — vendor builds its own DOM; we
  // only need a host node. Guarded by !hasPrimaryHtml (Storycards regression).
  if (hasScripts && !hasMarkup && !hasPrimaryHtml) {
    return (
      <ErrorBoundary>
        {scriptTags}
        <div data-mm-id={dataId} className={css(additionalStyle.style, style)} />
      </ErrorBoundary>
    );
  }

  // Mode 3: HTML + scripts — inline the embed's own markup so the vendor SDK finds and
  // sizes its container (e.g. Storycards sizes via its wrapper height + data-height-* per
  // device). Stay out of the way: no aspect-ratio Iframe box, which would double-count
  // height and leave a gap. The Iframe path below is only for bare, script-less iframes.
  if (hasScripts && (hasMarkup || hasPrimaryHtml)) {
    const inlineHtml = markup || primaryHtml;
    return (
      <ErrorBoundary>
        {scriptTags}
        {/* eslint-disable-next-line react/no-danger */}
        <figure data-mm-id={dataId} className={css(additionalStyle.style, style)} dangerouslySetInnerHTML={{ __html: inlineHtml }} />
      </ErrorBoundary>
    );
  }

  // Mode 1: render primary HTML (or markup fallback) via the lazy Iframe primitive.
  // Reached only when there are no companion scripts.
  const html = primaryHtml || markup;
  if (!html) return null;
  return (
    <ErrorBoundary>
      {scriptTags}
      <Iframe
        html={html}
        dataId={dataId}
        additionalStyle={{
          ...additionalStyle,
          style: {
            ...additionalStyle.style,
            paddingTop: '0px',
          },
        }}
      />
    </ErrorBoundary>
  );
};
